/**
 * Past-track recording.
 *
 * RYA convention: when the simulator is running, the boat lays down a track of
 * positions with timestamps. Most marine plotters draw "primary" and
 * "secondary" tracks, where the primary is the live current run and the
 * secondary is a previous saved run (used by instructors comparing attempts).
 *
 * For phase 1 we model both as simple time-stamped point arrays. The renderer
 * draws them as lines with periodic time-marks.
 */

/**
 * Tiered cadence for the past-track time-marks, all measured as elapsed minutes
 * from the track's start (t0). We drop marks close together early on (when a
 * manoeuvre is happening) and space them out as the run gets long, so a
 * multi-hour passage doesn't turn into a wall of labels.
 *
 * Each tier reads "up to `untilMin` elapsed, place a mark every `stepMin`". The
 * last tier uses Infinity so it runs to the end of any track. These are the only
 * knobs, so the schedule is easy to retune here without touching the logic.
 *
 *   0 -> 60 min:   every 6 min   (6, 12, 18 ... 60)
 *   60 -> 180 min: every 15 min  (75, 90 ... 180)
 *   180 -> 360 min: every 30 min (210, 240 ... 360)
 *   beyond 360 min: every 60 min (420, 480 ...)
 */
export interface TimeMarkTier {
  /** Place marks up to and including this elapsed minute (Infinity = no cap). */
  untilMin: number;
  /** Minutes between marks within this tier. */
  stepMin: number;
}

export const TIME_MARK_TIERS: readonly TimeMarkTier[] = [
  { untilMin: 60, stepMin: 6 },
  { untilMin: 180, stepMin: 15 },
  { untilMin: 360, stepMin: 30 },
  { untilMin: Infinity, stepMin: 60 },
];

export interface TrackPoint {
  /** [lon, lat] WGS84. */
  position: [number, number];
  /** Unix ms timestamp (or scenario-relative time when simulated). */
  t: number;
  /**
   * Starts a new track segment here: the drawn line BREAKS before this point
   * instead of joining to the previous one. Set when the position source is
   * switched, so the breadcrumb never draws an impossible jump (e.g. from the
   * fictional sim area to a real GNSS fix).
   */
  gap?: boolean;
}

export interface Track {
  /** Stable id for layer keying. */
  id: string;
  points: TrackPoint[];
  /** Drawing colour family — primary or secondary. */
  kind: 'primary' | 'secondary';
}

/**
 * The most points the live breadcrumb keeps, oldest dropped first.
 *
 * There was no limit at all, which is fine right up until it is not: the trail
 * is appended to for as long as the app runs, and the app now runs all night on
 * an anchor watch and all week on a passage.
 *
 * Twenty thousand. At the 9 m gate below that is around 100 miles of coast
 * behind us, and at the once-a-minute floor a boat sitting still would take
 * close to a fortnight to reach it. The number is also held down by HOW an
 * append works: each one copies the array, so the cost of a fix is the length
 * of the track, and a cap ten times this size makes every fix ten times dearer
 * whether or not memory ever became the problem. Saving a track keeps it whole
 * and separate, so a passage worth keeping is never at the mercy of this.
 */
export const MAX_TRACK_POINTS = 20_000;

/**
 * Append a point to a track, with a minimum-distance gate so a still boat
 * doesn't fill up memory with identical points.
 *
 * Returns the SAME track object when the gate rejects the point. Callers lean on
 * that: the ownship layer caches its drawn geometry on the track's identity, so
 * an unchanged track costs nothing to redraw.
 */
export function appendTrackPoint(
  track: Track,
  point: TrackPoint,
  minStepNm = 0.005, // ~9 m
  maxPoints = MAX_TRACK_POINTS,
): Track {
  if (!track.points.length) return { ...track, points: [point] };
  const last = track.points[track.points.length - 1];
  const dx = (point.position[0] - last.position[0]) * Math.cos((last.position[1] * Math.PI) / 180);
  const dy = point.position[1] - last.position[1];
  const dNm = Math.hypot(dx, dy) * 60;
  if (dNm < minStepNm && point.t - last.t < 60_000) return track;
  const points = [...track.points, point];
  if (points.length > maxPoints) points.splice(0, points.length - maxPoints);
  return { ...track, points };
}

/**
 * Split a track's points into continuous segments, breaking wherever a point is
 * flagged `gap` (a source change). Each segment is drawn as its own line, so the
 * breadcrumb never joins across a break.
 */
export function trackSegments(track: Track): TrackPoint[][] {
  const segments: TrackPoint[][] = [];
  let cur: TrackPoint[] = [];
  for (const p of track.points) {
    if (p.gap && cur.length) {
      segments.push(cur);
      cur = [];
    }
    cur.push(p);
  }
  if (cur.length) segments.push(cur);
  return segments;
}

/**
 * The elapsed-minute offsets at which time-marks fall, for a track that has run
 * for `maxElapsedMin` minutes. Pure: no positions, no track, just the schedule,
 * so it's trivial to unit-test and retune. Walks the tiers above, stepping the
 * interval up as the run gets longer, and stops as soon as an offset would run
 * past the track's actual length.
 *
 * e.g. a 7-hour run yields 6, 12 ... 60, 75, 90 ... 180, 210 ... 360, 420 ...
 */
export function timeMarkOffsetsMin(maxElapsedMin: number): number[] {
  const offsets: number[] = [];
  let prevBoundary = 0;
  for (const tier of TIME_MARK_TIERS) {
    for (let m = prevBoundary + tier.stepMin; m <= tier.untilMin; m += tier.stepMin) {
      if (m > maxElapsedMin) return offsets;
      offsets.push(m);
    }
    prevBoundary = tier.untilMin;
    if (!Number.isFinite(tier.untilMin)) break;
  }
  return offsets;
}

/**
 * Turn an elapsed time (minutes from track start) into its mark label. Under an
 * hour we say plain minutes ("6 min", "12 min") so it can't be misread as the
 * metres GNSS readout sitting next to it. At an hour and beyond we switch to
 * hours for readability: whole hours read "1 h", "2 h"; part-hours read "1 h 15",
 * "1 h 30" (hours then minutes, minutes zero-padded to two digits).
 */
export function formatElapsedLabel(mins: number): string {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (m === 0) return `${h} h`;
  return `${h} h ${String(m).padStart(2, '0')}`;
}

/**
 * Place time-marks along a track, returning { position, label, t } for each.
 *
 * The schedule is anchored to the FIRST point's timestamp (t0): every mark is at
 * a fixed elapsed offset from t0 (see timeMarkOffsetsMin / TIME_MARK_TIERS), not
 * at wall-clock 6-minute boundaries. So a fresh track's first mark is at exactly
 * 6 minutes after it started, the next at 12, and so on. We don't mark t0 itself,
 * the start point is obvious without a "0 min" label.
 *
 * A mark's position is interpolated along the track at its target time. Where the
 * track breaks (a point flagged `gap`, e.g. a source change) the span across the
 * break isn't real track, so any mark that would land inside it is skipped rather
 * than drawn across the impossible jump.
 */
export function timeMarks(
  track: Track,
): { position: [number, number]; label: string; t: number }[] {
  const pts = track.points;
  if (pts.length < 2) return [];
  const t0 = pts[0].t;
  const tEnd = pts[pts.length - 1].t;
  const maxElapsedMin = (tEnd - t0) / 60_000;
  const offsets = timeMarkOffsetsMin(maxElapsedMin);

  const out: { position: [number, number]; label: string; t: number }[] = [];
  let oi = 0;
  for (let i = 1; i < pts.length && oi < offsets.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    // Consume every scheduled mark whose target time falls in (a.t, b.t]. The
    // lower bound is open because a mark exactly at a.t was already placed by the
    // previous segment (or is t0, which we skip).
    while (oi < offsets.length) {
      const targetT = t0 + offsets[oi] * 60_000;
      if (targetT > b.t) break;
      // b starts a new segment: a -> b is a break span, not real track. Any mark
      // strictly inside it is skipped; one landing exactly on b (a real point) is
      // still placed.
      if (b.gap && targetT < b.t) {
        oi++;
        continue;
      }
      const f = b.t === a.t ? 0 : (targetT - a.t) / (b.t - a.t);
      const lon = a.position[0] + (b.position[0] - a.position[0]) * f;
      const lat = a.position[1] + (b.position[1] - a.position[1]) * f;
      out.push({ position: [lon, lat], label: formatElapsedLabel(offsets[oi]), t: targetT });
      oi++;
    }
  }
  return out;
}
